import * as React from "react"
import { CalendarIcon } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useEffectiveDocumentDir } from "@/hooks/useEffectiveDocumentDir"

function formatDate(date) {
  if (!date) return "";

  const d = new Date(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function isValidDate(date) {
  return date && !isNaN(date.getTime())
}

export function Calendar28({
  label,
  className,
  disablePastDates,
  required = false,
  error,
  value: controlledValue,
  onChange,
  name,
}) {
  const { t } = useTranslation()
  const dir = useEffectiveDocumentDir()
  const isRtl = dir === "rtl"
  const cal = "common.calendar."
  const fieldId = React.useId()

  const [open, setOpen] = React.useState(false)
  const initial = controlledValue ? new Date(controlledValue) : null
  const [date, setDate] = React.useState(
    initial && isValidDate(initial) ? initial : null,
  )
  const [month, setMonth] = React.useState(date || new Date())
  const [value, setValue] = React.useState(
    date && isValidDate(date) ? formatDate(date) : "",
  )

  const today = React.useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return now
  }, [])

  React.useEffect(() => {
    if (controlledValue === undefined) return
    if (
      controlledValue === null ||
      controlledValue === ""
    ) {
      setDate(null)
      setValue("")
      return
    }
    const next = new Date(controlledValue)
    if (!isValidDate(next)) return
    setDate(next)
    setValue(formatDate(next))
    setMonth(next)
  }, [controlledValue])

  const handleDateSelect = (selected) => {
    if (!selected) return
    setDate(selected)
    setValue(formatDate(selected))
    setOpen(false)
    onChange?.(formatDate(selected))
  }

  return (
    <div className="flex flex-col gap-3">
      {label && (
        <Label htmlFor={`${fieldId}-date`} className="px-1 text-secondary ">
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}

      <div className="relative flex gap-2">
        <Input
          id={`${fieldId}-date`}
          value={value || formatDate(controlledValue) || ""}
          name={name}
          placeholder={t(`${cal}placeholder`)}
          className={`bg-background pe-10 ${className} ${error ? "border-red-500" : ""}`}
          onChange={(e) => {
            const d = new Date(e.target.value)
            setValue(e.target.value)
            if (isValidDate(d)) {
              setDate(d)
              setMonth(d)
              onChange?.(formatDate(d))
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault()
              setOpen(true)
            }
          }}
        />

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={`${fieldId}-picker`}
              variant="ghost"
              type="button"
              aria-label={t(`${cal}openPickerAria`)}
              className="absolute top-1/2 end-4 size-6 border-input -translate-y-1/2"
            >
              <CalendarIcon className="size-3.5" />
              <span className="sr-only">{t(`${cal}selectDateAria`)}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent
            dir={dir}
            align={isRtl ? "start" : "end"}
            className="w-auto overflow-hidden p-0 bg-white"
            alignOffset={isRtl ? 8 : -8}
            sideOffset={10}
          >
            <Calendar
              mode="single"
              selected={date}
              month={month}
              onMonthChange={setMonth}
              onSelect={handleDateSelect}
              captionLayout="dropdown"
              {...(disablePastDates ? { disabled: { before: today } } : {})}
            />
          </PopoverContent>
        </Popover>
      </div>

      {error && <p className="text-red-500 text-xs mt-1 px-1">{error}</p>}
    </div>
  )
}
